import { cloneDeep } from 'lodash';
import { MIN_FONT_SIZE, type IChartLayer, type IChartLayerProps, type ILayerBehavior } from '@/version-types';

type Behaviors = Pick<
    ILayerBehavior,
    | 'scaleOperateStyle'
    | 'resizeOperateStyle'
    | 'onBeforeScaleUpdate'
    | 'onBaseScaleEnd'
    | 'onBeforeResizeUpdate'
    | 'onBeforeCrop'
    | 'onBeforeAddOrUpdate'
    | 'isDefaultUnequalStretch'
    | 'getScaleIsNeedLimitSize'
    | 'getLayerMinSize'
    | 'checkDisableFlip'
>;

const scaleLayerData = (layerData: IChartLayerProps, scale: number): IChartLayerProps => {
    const newLayerData = cloneDeep(layerData);
    (['legend', 'categoryAxisLabel', 'valueAxisLabel'] as const).forEach((key) => {
        newLayerData[key].textStyle.fontSize = Math.ceil(
            Math.max(newLayerData[key].textStyle.fontSize * scale, MIN_FONT_SIZE),
        );
    });

    newLayerData.series.label.textStyle.fontSize = Math.ceil(
        Math.max(newLayerData.series.label.textStyle.fontSize * scale, MIN_FONT_SIZE),
    );
    return newLayerData;
};

export const createOperationBehaviors = (_frameworkIdentity: string) => {
    const beforeScaleLayerMap: Map<string, IChartLayer> = new Map();

    const behaviors: Behaviors = {
        onBeforeScaleUpdate(_expectLayer, { previousTickLayer: _previousTickLayer, isStart, isFromAdjustScale }) {
            const expectLayer = _expectLayer as IChartLayer;
            const previousTickLayer = _previousTickLayer as IChartLayer;

            let beforeScaleLayer = beforeScaleLayerMap.get(previousTickLayer.id);

            if (isStart || isFromAdjustScale || !beforeScaleLayer) {
                // 鼠标缩放的 start 帧只标记基础操作状态, 不会进入 updateLayerScale.
                beforeScaleLayer = cloneDeep(previousTickLayer);
                beforeScaleLayerMap.set(previousTickLayer.id, beforeScaleLayer);
            }

            const scale = expectLayer.width / beforeScaleLayer.width;

            const newLayer = {
                ...cloneDeep(expectLayer),
                data: scaleLayerData(beforeScaleLayer.data, scale),
            };

            // 下面的操作相当于把 newLayer 赋值给 layer
            Object.keys(expectLayer).forEach((key) => delete expectLayer[key]);
            Object.assign(expectLayer, newLayer);
        },

        async onBaseScaleEnd() {
            beforeScaleLayerMap.clear();
            return false;
        },

        isDefaultUnequalStretch: () => true,

        getScaleIsNeedLimitSize: () => true,

        checkDisableFlip() {
            return true;
        },

        getLayerMinSize(_, _layer) {
            const layer = _layer as IChartLayer;
            const { legend, categoryAxisLabel, valueAxisLabel, series } = layer.data;
            let minFontSize: number | null = null;
            if (legend.show) {
                minFontSize = minFontSize
                    ? Math.min(minFontSize, legend.textStyle.fontSize)
                    : legend.textStyle.fontSize;
            }
            if (categoryAxisLabel.show) {
                minFontSize = minFontSize
                    ? Math.min(minFontSize, categoryAxisLabel.textStyle.fontSize)
                    : categoryAxisLabel.textStyle.fontSize;
            }
            if (valueAxisLabel.show) {
                minFontSize = minFontSize
                    ? Math.min(minFontSize, valueAxisLabel.textStyle.fontSize)
                    : valueAxisLabel.textStyle.fontSize;
            }
            if (series.label.show) {
                minFontSize = minFontSize
                    ? Math.min(minFontSize, series.label.textStyle.fontSize)
                    : series.label.textStyle.fontSize;
            }
            if (!minFontSize) {
                return {
                    width: 0,
                    height: 0,
                };
            }
            const minRatio = MIN_FONT_SIZE / minFontSize;
            return {
                width: layer.width * minRatio,
                height: layer.height * minRatio,
            };
        },
    };

    return behaviors;
};
